// client/src/components/admin/TaskManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateTaskMutation,
  useGetAssignedTasksQuery,
  useVerifyTaskMutation,
  useGetFieldEmployeesQuery,
} from "../../redux/admin/taskApi";
import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import { useStatus } from "../../popup/NotificationContext";

// ✅ MUI
import {
  Box,
  Paper,
  Stack,
  Typography,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  InputAdornment,
  Tooltip,
  Grid,
  Pagination,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ScrollableTableContainer from "../common/ScrollableTableContainer";

// lucide
import { RefreshCw, X, MapPin, Search } from "lucide-react";
import { MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import { useListItemsQuery } from "../../redux/admin/inventoryApi";

const TaskManagement = ({
  forcedView = null,
  hideShell = false,
  embedded = false,
  onNavigateToCreate,
  onNavigateToList,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { showStatus } = useStatus();

  const [activeTab, setActiveTab] = useState(forcedView || "create");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [taskPage, setTaskPage] = useState(1);
  const [taskRowsPerPage, setTaskRowsPerPage] = useState(10);

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  const [vehicleSearchQuery, setVehicleSearchQuery] = useState("");
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false);

  const addressAnchorRef = useRef(null);
  const vehicleAnchorRef = useRef(null);
  

  // API
  const {
    data: tasks = [],
    isLoading: tasksLoading,
    refetch,
  } = useGetAssignedTasksQuery({ status: filterStatus });

  const { data: fieldEmployees = [] } = useGetFieldEmployeesQuery();
  const { data: devicesResponse = [] } = useGetAllLiteDevicesQuery();

  const [createTask, { isLoading: creating }] = useCreateTaskMutation();
  const [verifyTask, { isLoading: verifying }] = useVerifyTaskMutation();
  const { data: invData, isLoading: invLoading } = useListItemsQuery({ q: "" });




    // Form state
 const [formData, setFormData] = useState({
  taskType: "gps_install",
  title: "",
  description: "",
  assignedTo: "",
  siteAddress: "",
  siteLatitude: "",
  siteLongitude: "",
  clientName: "",
  clientPhone: "",
  deviceImei: "",
  vehicleNumber: "",
  scheduledDate: "",
  scheduledTime: "",
  priority: "medium",

  // ✅ Inventory (NEW)
  inventoryRequired: false,
  inventoryIssue: {
    itemId: "",
    serial: "", // for serialized
    qty: 1,     // for bulk
  },
  inventoryHandover: {
    ownerType: "vehicle", // warehouse/dealer/employee/user/device/vehicle
    ownerId: "",          // only for non-vehicle types (optional)
    ownerLabel: "",       // required for vehicle (DL01AB1234)
  },
});


  const inventoryItems = useMemo(() => {
  return Array.isArray(invData?.items) ? invData.items : [];
}, [invData]);

const selectedInvItem = useMemo(() => {
  const itemId = formData?.inventoryIssue?.itemId;
  if (!itemId) return null;
  return inventoryItems.find((x) => String(x._id) === String(itemId)) || null;
}, [inventoryItems, formData?.inventoryIssue?.itemId]);


const isSerializedSelected = !!selectedInvItem?.isSerialized;
  // ✅ Extract devices array safely
  const devices = useMemo(() => {
    if (Array.isArray(devicesResponse)) return devicesResponse;
    if (devicesResponse?.data && Array.isArray(devicesResponse.data)) return devicesResponse.data;
    return [];
  }, [devicesResponse]);

  const [verifyData, setVerifyData] = useState({
    verificationStatus: "approved",
    verificationNotes: "",
  });

  // ✅ Filter registered vehicles based on search (same logic)
  const filteredVehicles = useMemo(() => {
    if (!vehicleSearchQuery.trim()) return [];
    const query = vehicleSearchQuery.toLowerCase();
    return devices
      .filter(
        (device) =>
          device.name?.toLowerCase().includes(query) ||
          device.imei?.toLowerCase().includes(query) ||
          device.model?.toLowerCase().includes(query) ||
          device.vehicleType?.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [devices, vehicleSearchQuery]);

  // ✅ Google Places suggestions (same feature)
  const fetchAddressSuggestions = async (input) => {
    if (!input || input.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        { input, componentRestrictions: { country: "in" } },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setAddressSuggestions(predictions);
          } else {
            setAddressSuggestions([]);
          }
        }
      );
    } catch (error) {
      console.error("Address suggestion error:", error);
      setAddressSuggestions([]);
    }
  };

  const getPlaceDetails = async (placeId) => {
    try {
      const service = new window.google.maps.places.PlacesService(document.createElement("div"));
      service.getDetails({ placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          setFormData((prev) => ({
            ...prev,
            siteAddress: place.formatted_address,
            siteLatitude: place.geometry.location.lat(),
            siteLongitude: place.geometry.location.lng(),
          }));
          setShowAddressSuggestions(false);
        }
      });
    } catch (error) {
      console.error("Place details error:", error);
    }
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            setFormData((prev) => ({
              ...prev,
              siteAddress: results[0].formatted_address,
            }));
          }
        }
      );
    } catch (error) {
      console.error("Reverse geocode error:", error);
    }
  };

const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target;

  console.log("📝 INPUT CHANGE:", { name, value, type, checked }); // ✅ DEBUG

  // ✅ Special: Inventory toggle ON/OFF => nested reset
  if (type === "checkbox" && name === "inventoryRequired") {
    setFormData((prev) => ({
      ...prev,
      inventoryRequired: checked,
      inventoryIssue: checked ? prev.inventoryIssue : { itemId: "", serial: "", qty: 1 },
      inventoryHandover: checked ? prev.inventoryHandover : { ownerType: "vehicle", ownerId: null, ownerLabel: "" },
    }));
    return;
  }

  // ✅ other checkboxes
  if (type === "checkbox") {
    setFormData((prev) => ({ ...prev, [name]: checked }));
    return;
  }

  // ✅ nested inventoryIssue fields
  if (name.startsWith("inventoryIssue.")) {
    const key = name.split(".")[1];
    
    // ✅ CRITICAL FIX: itemId should be stored as-is, not processed
    const finalValue = key === "qty" ? Number(value || 1) : value;
    
    console.log(`📦 Setting inventoryIssue.${key} =`, finalValue); // ✅ DEBUG
    
    setFormData((prev) => ({
      ...prev,
      inventoryIssue: {
        ...prev.inventoryIssue,
        [key]: finalValue,
      },
    }));
    return;
  }

  // ✅ nested inventoryHandover fields
  if (name.startsWith("inventoryHandover.")) {
    const key = name.split(".")[1];
    setFormData((prev) => ({
      ...prev,
      inventoryHandover: {
        ...prev.inventoryHandover,
        [key]: value,
      },
    }));
    return;
  }

  // ✅ normal fields
  setFormData((prev) => ({ ...prev, [name]: value }));

  // address suggestions
  if (name === "siteAddress") {
    setShowAddressSuggestions(true);
    fetchAddressSuggestions(value);
  }
};


const handleCreateTask = async (e) => {
  e.preventDefault();

  if (!formData.assignedTo) {
    showStatus("warning", "Please select a field employee to assign the task.");
    return;
  }

  // ✅ Inventory validations
  if (formData.inventoryRequired) {
    const itemId = String(formData.inventoryIssue?.itemId || "").trim();
    if (!itemId) {
      showStatus("warning", "Inventory item is required when Inventory Required is ON.");
      return;
    }

    if (isSerializedSelected) {
      const serial = String(formData.inventoryIssue?.serial || "").trim();
      if (!serial) {
        showStatus("warning", "Serial/IMEI is required for serialized inventory item.");
        return;
      }
    } else {
      const qty = Number(formData.inventoryIssue?.qty || 0);
      if (!qty || qty < 1) {
        showStatus("warning", "Qty must be at least 1 for bulk inventory item.");
        return;
      }
    }

    // vehicle handover -> ownerLabel required
    if ((formData.inventoryHandover?.ownerType || "vehicle") === "vehicle") {
      const label = String(formData.inventoryHandover?.ownerLabel || "").trim();
      if (!label) {
        showStatus("warning", "Vehicle Number (Owner Label) is required for vehicle handover.");
        return;
      }
    }
  }

  try {
    // ✅ Build CLEAN payload - explicit fields only
    const payload = {
      taskType: formData.taskType,
      title: formData.title,
      description: formData.description,
      assignedTo: formData.assignedTo,
      siteAddress: formData.siteAddress,
      siteLatitude: formData.siteLatitude,
      siteLongitude: formData.siteLongitude,
      clientName: formData.clientName,
      clientPhone: formData.clientPhone,
      deviceImei: formData.deviceImei,
      vehicleNumber: formData.vehicleNumber,
      scheduledDate: formData.scheduledDate,
      scheduledTime: formData.scheduledTime,
      priority: formData.priority,
    };

    // ✅ ONLY add inventory if required AND itemId exists
    if (formData.inventoryRequired && formData.inventoryIssue?.itemId) {
      payload.inventoryRequired = true;
      
      payload.inventoryIssue = {
        itemId: String(formData.inventoryIssue.itemId).trim(),
        serial: String(formData.inventoryIssue.serial || "").trim(),
        qty: Number(formData.inventoryIssue.qty || 1),
      };

      const ownerType = formData.inventoryHandover?.ownerType || "vehicle";
      payload.inventoryHandover = {
        ownerType,
        ownerLabel: String(formData.inventoryHandover?.ownerLabel || "").trim(),
      };

      // Only add ownerId if NOT vehicle and exists
      if (ownerType !== "vehicle" && formData.inventoryHandover?.ownerId) {
        payload.inventoryHandover.ownerId = formData.inventoryHandover.ownerId;
      }
    }

    console.log("✅ CREATE TASK PAYLOAD =>", JSON.stringify(payload, null, 2));

    await createTask(payload).unwrap();

    showStatus("success", "Task created and assigned successfully!");

    // reset
    setFormData({
      taskType: "gps_install",
      title: "",
      description: "",
      assignedTo: "",
      siteAddress: "",
      siteLatitude: "",
      siteLongitude: "",
      clientName: "",
      clientPhone: "",
      deviceImei: "",
      vehicleNumber: "",
      scheduledDate: "",
      scheduledTime: "",
      priority: "medium",
      inventoryRequired: false,
      inventoryIssue: { itemId: "", serial: "", qty: 1 },
      inventoryHandover: { ownerType: "vehicle", ownerId: null, ownerLabel: "" },
    });

    setVehicleSearchQuery("");
    refetch();
  } catch (err) {
    console.error("❌ CREATE TASK ERROR:", err);
    const msg =
      err?.data?.msg ||
      (Array.isArray(err?.data?.error) ? err.data.error.join(", ") : "") ||
      "Failed to create task. Check required fields.";
    showStatus("error", msg);
  }
};

  // coordinate -> reverse geocode (same feature)
  useEffect(() => {
    if (formData.siteLatitude && formData.siteLongitude) {
      const lat = parseFloat(formData.siteLatitude);
      const lng = parseFloat(formData.siteLongitude);
      if (!isNaN(lat) && !isNaN(lng)) reverseGeocode(lat, lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.siteLatitude, formData.siteLongitude]);

  // vehicle select (same feature)
  const handleVehicleSelect = (device) => {
    setFormData((prev) => ({
      ...prev,
      vehicleNumber: device.name,
      deviceImei: device.imei || "",
    }));
    setVehicleSearchQuery(device.name);
    setShowVehicleSuggestions(false);
  };

  const handleVehicleInputChange = (e) => {
    const value = e.target.value;
    setVehicleSearchQuery(value);
    setFormData((prev) => ({ ...prev, vehicleNumber: value }));
    setShowVehicleSuggestions(true);
  };




  const handleVerify = async () => {
    if (!selectedTask) return;
    try {
      await verifyTask({
        taskId: selectedTask._id,
        ...verifyData,
      }).unwrap();

      showStatus("success", `Task for ${selectedTask.assignedTo?.username} verified as ${verifyData.verificationStatus}.`);

      setShowVerifyModal(false);
      setSelectedTask(null);
      refetch();
    } catch (err) {
      showStatus("error", err?.data?.msg || "Verification failed. Please try again.");
    }
  };

  const openVerifyModal = (task) => {
    setSelectedTask(task);
    setVerifyData({ verificationStatus: "approved", verificationNotes: "" });
    setShowVerifyModal(true);
  };

  const statusChip = (status) => {
    const map = {
      pending: { label: "Pending", tone: "warning" },
      accepted: { label: "Accepted", tone: "success" },
      in_progress: { label: "In Progress", tone: "primary" },
      completed: { label: "Completed", tone: "success" },
      rejected: { label: "Rejected", tone: "error" },
      cancelled: { label: "Cancelled", tone: "default" },
    };
    const s = map[status] || { label: status || "—", tone: "default" };
    return <Chip size="small" label={s.label} color={s.tone} sx={{ fontWeight: 900, borderRadius: 2 }} />;
  };

  const priorityChip = (priority) => {
    const map = {
      low: { label: "Low", tone: "success" },
      medium: { label: "Medium", tone: "warning" },
      high: { label: "High", tone: "warning" },
      urgent: { label: "Urgent", tone: "error" },
    };
    const p = map[priority] || map.medium;
    return <Chip size="small" label={p.label} color={p.tone} sx={{ fontWeight: 900, borderRadius: 2 }} />;
  };

  const taskTypeLabel = (task) => normalizedTaskType(task?.taskType);

  // close suggestion dropdowns on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      const a = addressAnchorRef.current;
      const v = vehicleAnchorRef.current;

      if (a && !a.contains(e.target)) setShowAddressSuggestions(false);
      if (v && !v.contains(e.target)) setShowVehicleSuggestions(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fieldEmployeeOptions = fieldEmployees?.data || fieldEmployees || [];

  useEffect(() => {
    if (forcedView) {
      setActiveTab(forcedView);
    }
  }, [forcedView]);

  const filteredTasks = useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return tasks;
    return tasks.filter((task) =>
      [
        task?.title,
        task?.taskType,
        task?.vehicleNumber,
        task?.deviceImei,
        task?.assignedTo?.username,
        task?.siteAddress,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [searchQuery, tasks]);

  const totalTaskPages = Math.max(1, Math.ceil(filteredTasks.length / taskRowsPerPage));
  const pagedTasks = useMemo(() => {
    const start = (taskPage - 1) * taskRowsPerPage;
    return filteredTasks.slice(start, start + taskRowsPerPage);
  }, [filteredTasks, taskPage, taskRowsPerPage]);

  useEffect(() => {
    if (taskPage > totalTaskPages) setTaskPage(1);
  }, [taskPage, totalTaskPages]);

  const normalizedTaskType = (taskType) => String(taskType || "").replaceAll("_", " ");

  return (
    <Box sx={{ p: embedded ? 0 : { xs: 1.2, md: 2.5 } }}>
      {!hideShell ? (
        <>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.6, md: 2.2 },
              borderRadius: 4,
              borderColor: alpha(theme.palette.divider, 0.9),
              mb: 2,
            }}
          >
            <Typography sx={{ fontWeight: 1000, fontSize: { xs: 20, md: 26 } }}>
              Task Management
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Assign and manage field tasks
            </Typography>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              borderColor: alpha(theme.palette.divider, 0.9),
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant={isSmDown ? "fullWidth" : "standard"}
              sx={{ px: 1 }}
            >
              <Tab value="create" label="Create Task" />
              <Tab value="list" label="All Tasks" />
            </Tabs>
            <Divider />
          </Paper>
        </>
      ) : null}

      {/* CREATE TAB */}
      {activeTab === "create" && (
        <Box component="form" onSubmit={handleCreateTask}>
          <Stack spacing={2}>
            {hideShell ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="flex-end"
                spacing={1.2}
              >
                <Button
                  variant="outlined"
                  onClick={() => onNavigateToList?.()}
                  sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={creating}
                  sx={{ borderRadius: 2.5, fontWeight: 900, textTransform: "none", px: 2.8 }}
                >
                  {creating ? "Creating..." : "Create Task"}
                </Button>
              </Stack>
            ) : null}

            <Grid container spacing={2}>
              <Grid item xs={12} xl={9}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    borderColor: alpha(theme.palette.divider, 0.9),
                    p: { xs: 1.6, md: 2.2 },
                    boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>
                    Basic Information
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 14.5, mt: 0.6, mb: 2 }}>
                    Add task details, client info, vehicle selection and job site address.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Task Type *"
                        name="taskType"
                        value={formData.taskType}
                        onChange={handleInputChange}
                        required
                      >
                        <MenuItem value="gps_install">GPS Install</MenuItem>
                        <MenuItem value="device_install">Device Install</MenuItem>
                        <MenuItem value="gps_repair">GPS Repair</MenuItem>
                        <MenuItem value="sim_change">SIM Change</MenuItem>
                        <MenuItem value="device_replace">Device Replace</MenuItem>
                        <MenuItem value="site_visit">Site Visit</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Title *"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter task title"
                        required
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Assigned To *"
                        name="assignedTo"
                        value={formData.assignedTo}
                        onChange={handleInputChange}
                        required
                      >
                        <MenuItem value="">Select Field Employee</MenuItem>
                        {fieldEmployeeOptions.map((emp) => (
                          <MenuItem key={emp._id} value={emp._id}>
                            {emp.username} - {emp.mobileNo}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Client Name"
                        name="clientName"
                        value={formData.clientName}
                        onChange={handleInputChange}
                        placeholder="Enter client name"
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Client Phone"
                        name="clientPhone"
                        value={formData.clientPhone}
                        onChange={handleInputChange}
                        placeholder="Enter client phone"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter task description"
                        multiline
                        minRows={3}
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Box ref={vehicleAnchorRef} sx={{ position: "relative" }}>
                        <TextField
                          fullWidth
                          label="Vehicle Number"
                          value={vehicleSearchQuery}
                          onChange={handleVehicleInputChange}
                          onFocus={() => setShowVehicleSuggestions(true)}
                          placeholder="Search registered vehicle"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Search size={18} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {showVehicleSuggestions && filteredVehicles.length > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{
                              position: "absolute",
                              zIndex: 20,
                              left: 0,
                              right: 0,
                              mt: 1,
                              borderRadius: 3,
                              overflow: "hidden",
                              borderColor: alpha(theme.palette.divider, 0.9),
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: "0 18px 32px rgba(15,23,42,0.12)",
                            }}
                          >
                            {filteredVehicles.map((device) => (
                              <Box
                                key={device._id}
                                onClick={() => handleVehicleSelect(device)}
                                sx={{
                                  px: 1.6,
                                  py: 1.2,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                                }}
                              >
                                <Typography sx={{ fontWeight: 900 }}>{device.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {device.model || "—"} • {device.vehicleType || "N/A"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  IMEI: {device.imei || "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={7}>
                      <Box ref={addressAnchorRef} sx={{ position: "relative" }}>
                        <TextField
                          fullWidth
                          label="Site Address *"
                          name="siteAddress"
                          value={formData.siteAddress}
                          onChange={handleInputChange}
                          onFocus={() => setShowAddressSuggestions(true)}
                          placeholder="Start typing address..."
                          required
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <MapPin size={18} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        {showAddressSuggestions && addressSuggestions.length > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{
                              position: "absolute",
                              zIndex: 20,
                              left: 0,
                              right: 0,
                              mt: 1,
                              borderRadius: 3,
                              overflow: "hidden",
                              borderColor: alpha(theme.palette.divider, 0.9),
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: "0 18px 32px rgba(15,23,42,0.12)",
                            }}
                          >
                            {addressSuggestions.map((s) => (
                              <Box
                                key={s.place_id}
                                onClick={() => getPlaceDetails(s.place_id)}
                                sx={{
                                  px: 1.6,
                                  py: 1.2,
                                  cursor: "pointer",
                                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {s.description}
                                </Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} xl={3}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    borderColor: alpha(theme.palette.divider, 0.9),
                    p: { xs: 1.6, md: 2.2 },
                    height: "100%",
                    boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>
                    Schedule & Priority
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 14.5, mt: 0.6, mb: 2 }}>
                    Set planned visit timing and task urgency for field execution.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Scheduled Date *"
                        type="date"
                        name="scheduledDate"
                        value={formData.scheduledDate}
                        onChange={handleInputChange}
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Scheduled Time"
                        type="time"
                        name="scheduledTime"
                        value={formData.scheduledTime}
                        onChange={handleInputChange}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Priority *"
                        name="priority"
                        value={formData.priority}
                        onChange={handleInputChange}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} lg={7}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    borderColor: alpha(theme.palette.divider, 0.9),
                    p: { xs: 1.6, md: 2.2 },
                    boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>
                    Location & Device
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 14.5, mt: 0.6, mb: 2 }}>
                    Save exact coordinates and linked device details for the task site.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Latitude"
                        type="number"
                        inputProps={{ step: "any" }}
                        name="siteLatitude"
                        value={formData.siteLatitude}
                        onChange={handleInputChange}
                        placeholder="Enter latitude"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Longitude"
                        type="number"
                        inputProps={{ step: "any" }}
                        name="siteLongitude"
                        value={formData.siteLongitude}
                        onChange={handleInputChange}
                        placeholder="Enter longitude"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label={`Device IMEI ${formData.deviceImei ? "✓" : ""}`}
                        name="deviceImei"
                        value={formData.deviceImei}
                        onChange={handleInputChange}
                        placeholder="Enter device IMEI"
                        InputProps={{
                          readOnly:
                            !!vehicleSearchQuery &&
                            filteredVehicles.some((d) => d.name === vehicleSearchQuery),
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    borderColor: alpha(theme.palette.divider, 0.9),
                    p: { xs: 1.6, md: 2.2 },
                    boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 1.6 }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>
                        Inventory
                      </Typography>
                      <Typography sx={{ color: "#64748b", fontSize: 14.5, mt: 0.3 }}>
                        Optional
                      </Typography>
                    </Box>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!formData.inventoryRequired}
                          onChange={handleInputChange}
                          name="inventoryRequired"
                        />
                      }
                      label="Inventory Required"
                      sx={{ m: 0 }}
                    />
                  </Stack>

                  {formData.inventoryRequired ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Item *"
                          name="inventoryIssue.itemId"
                          value={formData.inventoryIssue?.itemId || ""}
                          onChange={handleInputChange}
                          disabled={invLoading}
                        >
                          <MenuItem value="">Select item</MenuItem>
                          {inventoryItems.map((it) => (
                            <MenuItem key={it._id} value={it._id}>
                              {it.name} ({it.isSerialized ? "Serialized" : "Bulk"})
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={3}>
                        {isSerializedSelected ? (
                          <TextField
                            fullWidth
                            label="Serial / IMEI *"
                            name="inventoryIssue.serial"
                            value={formData.inventoryIssue?.serial || ""}
                            onChange={handleInputChange}
                            placeholder="Enter serial"
                          />
                        ) : (
                          <TextField
                            fullWidth
                            type="number"
                            label="Quantity *"
                            name="inventoryIssue.qty"
                            value={formData.inventoryIssue?.qty || 1}
                            onChange={handleInputChange}
                            inputProps={{ min: 1 }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <TextField
                          select
                          fullWidth
                          label="Handover To"
                          name="inventoryHandover.ownerType"
                          value={formData.inventoryHandover?.ownerType || "vehicle"}
                          onChange={handleInputChange}
                        >
                          <MenuItem value="vehicle">Vehicle</MenuItem>
                          <MenuItem value="warehouse">Warehouse</MenuItem>
                          <MenuItem value="dealer">Dealer</MenuItem>
                          <MenuItem value="employee">Employee</MenuItem>
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="device">Device</MenuItem>
                        </TextField>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={
                            (formData.inventoryHandover?.ownerType || "vehicle") === "vehicle"
                              ? "Vehicle Number (Owner) *"
                              : "Owner Label"
                          }
                          name="inventoryHandover.ownerLabel"
                          value={formData.inventoryHandover?.ownerLabel || ""}
                          onChange={handleInputChange}
                          placeholder="e.g. DL01AB1234 / Rahul / Dealer A"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 3 }}>
                          Inventory will be issued automatically when field employee accepts the task, and handed-over on completion.
                        </Alert>
                      </Grid>
                    </Grid>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                      Turn on inventory only when a field employee needs a spare part, SIM, device or stock handover for this task.
                    </Alert>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {!hideShell ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={creating}
                  sx={{ borderRadius: 2.5, fontWeight: 900, textTransform: "none", px: 2.8 }}
                >
                  {creating ? "Creating..." : "Create Task"}
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      )}

      {/* LIST TAB */}
      {activeTab === "list" && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            borderColor: alpha(theme.palette.divider, 0.9),
            overflow: "hidden",
            boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
          }}
        >
          {/* Filters bar */}
          <Box sx={{ p: { xs: 1.4, md: 2 }, bgcolor: alpha(theme.palette.background.paper, 0.98) }}>
            {hideShell ? (
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.5} sx={{ mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>Task Management</Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 14.5 }}>Assign and manage field tasks</Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => onNavigateToCreate?.()}
                  sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 800 }}
                >
                  Create Task
                </Button>
              </Stack>
            ) : null}

            <Stack direction={{ xs: "column", lg: "row" }} spacing={1.2} alignItems={{ lg: "center" }} justifyContent="space-between">
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} sx={{ flex: 1 }}>
                <TextField
                  select
                  size="small"
                  label="Status Filter"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{ minWidth: { xs: "100%", md: 220 } }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="accepted">Accepted</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </TextField>

                <TextField
                  size="small"
                  placeholder="Search by title, type, vehicle, assignee..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setTaskPage(1);
                  }}
                  sx={{ flex: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search size={18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Button
                variant="outlined"
                onClick={refetch}
                startIcon={<RefreshCw size={18} />}
                sx={{ fontWeight: 900, borderRadius: 2.5, textTransform: "none" }}
              >
                Refresh
              </Button>
            </Stack>
          </Box>

          <Divider />

          {/* Content */}
          {tasksLoading ? (
            <Box sx={{ p: 5, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : filteredTasks.length === 0 ? (
            <Box sx={{ p: 5, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>No tasks found</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.7 }}>
                Try changing the status filter or search.
              </Typography>
            </Box>
          ) : (
            <>
            {isSmDown ? (
              <Stack spacing={1.2} sx={{ p: 1.4 }}>
                {pagedTasks.map((task) => (
                  <Paper key={task._id} variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>{task.title}</Typography>
                          <Typography variant="body2" color="text.secondary">{taskTypeLabel(task)}</Typography>
                        </Box>
                        <IconButton size="small">
                          <MoreHorizIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {statusChip(task.status)}
                        {priorityChip(task.priority)}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {task.assignedTo?.username || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {task.siteAddress || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {task.scheduledDate ? new Date(task.scheduledDate).toLocaleDateString() : "-"}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
            <ScrollableTableContainer sx={{ maxHeight: "calc(100vh - 290px)" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 1000, minWidth: 220 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Assigned To</TableCell>
                    <TableCell sx={{ fontWeight: 1000, minWidth: 260 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Schedule</TableCell>
                    <TableCell sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>Inventory</TableCell>
                    <TableCell sx={{ fontWeight: 1000, minWidth: 200 }}>Vehicle</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 1000, whiteSpace: "nowrap" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pagedTasks.map((task) => (
                    <TableRow key={task._id} hover>
                      <TableCell>{statusChip(task.status)}</TableCell>
                      <TableCell>{priorityChip(task.priority)}</TableCell>

                      <TableCell sx={{ fontWeight: 900 }}>
                        {task.title}
                      </TableCell>

                      <TableCell>
                        {taskTypeLabel(task)}
                      </TableCell>

                      <TableCell>
                        {task.assignedTo?.username || "N/A"}
                      </TableCell>

                      <TableCell>
                        <Tooltip title={task.siteAddress || ""}>
                          <Typography variant="body2" sx={{ maxWidth: 340, display: "flex", alignItems: "center", gap: 0.6 }} noWrap>
                            <MapPin size={14} />
                            {task.siteAddress ? `${task.siteAddress.slice(0, 60)}${task.siteAddress.length > 60 ? "…" : ""}` : "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {task.scheduledDate ? new Date(task.scheduledDate).toLocaleDateString() : "-"}
                        </Typography>
                        {task.scheduledTime && (
                          <Typography variant="caption" color="text.secondary">
                            {task.scheduledTime}
                          </Typography>
                        )}
                      </TableCell>
<TableCell>
  {task.inventoryRequired ? (
    <Stack spacing={0.4}>
      <Chip
        size="small"
        label={`Required • ${
          task.inventoryStatus
            ? String(task.inventoryStatus).replaceAll("_", " ")
            : "pending"
        }`}
        color={
          task.inventoryStatus === "handed_over"
            ? "success"
            : task.inventoryStatus === "issued"
            ? "info"
            : task.inventoryStatus === "returned"
            ? "warning"
            : task.inventoryStatus === "failed"
            ? "error"
            : "default"
        }
        sx={{ fontWeight: 900, borderRadius: 2, textTransform: "capitalize" }}
      />

      {task.inventoryIssue?.serial ? (
        <Typography variant="caption" color="text.secondary">
          Serial: {task.inventoryIssue.serial}
        </Typography>
      ) : task.inventoryIssue?.qty ? (
        <Typography variant="caption" color="text.secondary">
          Qty: {task.inventoryIssue.qty}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Item not issued yet
        </Typography>
      )}
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">—</Typography>
  )}
</TableCell>



                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {task.vehicleNumber || "-"}
                        </Typography>
                        {task.deviceImei && (
                          <Typography variant="caption" color="text.secondary">
                            IMEI: {task.deviceImei}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        {task.status === "completed" && task.verificationStatus === "pending" ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => openVerifyModal(task)}
                            sx={{ fontWeight: 900, borderRadius: 2 }}
                          >
                            Verify
                          </Button>
                        ) : task.verificationStatus && task.verificationStatus !== "pending" ? (
                          <Chip
                            size="small"
                            label={task.verificationStatus}
                            color={task.verificationStatus === "approved" ? "success" : "error"}
                            sx={{ fontWeight: 900, borderRadius: 2, textTransform: "capitalize" }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
            )}

            <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb", flexWrap: "wrap", gap: 1 }}>
              <Typography sx={{ color: "#475569", fontSize: 14 }}>
                Showing {filteredTasks.length ? (taskPage - 1) * taskRowsPerPage + 1 : 0} to {Math.min(taskPage * taskRowsPerPage, filteredTasks.length)} of {filteredTasks.length} tasks
              </Typography>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <TextField
                  select
                  size="small"
                  value={taskRowsPerPage}
                  onChange={(e) => {
                    setTaskRowsPerPage(Number(e.target.value));
                    setTaskPage(1);
                  }}
                  sx={{ minWidth: 110 }}
                >
                  {[10, 25, 50].map((size) => (
                    <MenuItem key={size} value={size}>{size} / page</MenuItem>
                  ))}
                </TextField>
                <Pagination page={taskPage} count={totalTaskPages} onChange={(_, value) => setTaskPage(value)} shape="rounded" />
              </Stack>
            </Box>
            </>
          )}
        </Paper>
      )}

      {/* VERIFY MODAL */}
      <Dialog
        open={showVerifyModal && Boolean(selectedTask)}
        onClose={() => setShowVerifyModal(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{ sx: { borderRadius: isSmDown ? 0 : 4 } }}
      >
        <DialogTitle sx={{ pb: 1.2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Typography sx={{ fontWeight: 1000 }}>Verify Task</Typography>
            <IconButton onClick={() => setShowVerifyModal(false)}>
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        {selectedTask && (
          <>
            <DialogContent sx={{ pt: 1 }}>
              <Stack spacing={1.2}>
                <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>{selectedTask.title}</Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Field Employee:</strong> {selectedTask.assignedTo?.username || "—"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  <strong>Completed At:</strong>{" "}
                  {selectedTask.completedAt ? new Date(selectedTask.completedAt).toLocaleString() : "—"}
                </Typography>

                {selectedTask.fieldNotes && (
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
                    <Typography sx={{ fontWeight: 900 }}>Field Notes</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {selectedTask.fieldNotes}
                    </Typography>
                  </Paper>
                )}

                {selectedTask.photos?.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 900, mb: 1 }}>Photos</Typography>
                    <Grid container spacing={1.2}>
                      {selectedTask.photos.map((photo, idx) => (
                        <Grid key={idx} item xs={6} sm={4} md={3}>
                          <Paper
                            variant="outlined"
                            sx={{
                              borderRadius: 3,
                              overflow: "hidden",
                              borderColor: alpha(theme.palette.divider, 0.9),
                            }}
                          >
                            <Box
                              component="img"
                              src={photo.url}
                              alt={photo.type}
                              sx={{ width: "100%", height: 120, objectFit: "cover", display: "block" }}
                            />
                            <Box sx={{ p: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {photo.type}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={5}>
                    <TextField
                      select
                      fullWidth
                      label="Verification Status"
                      value={verifyData.verificationStatus}
                      onChange={(e) =>
                        setVerifyData((prev) => ({ ...prev, verificationStatus: e.target.value }))
                      }
                    >
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={verifyData.verificationNotes}
                      onChange={(e) =>
                        setVerifyData((prev) => ({ ...prev, verificationNotes: e.target.value }))
                      }
                      placeholder="Add verification notes..."
                      multiline
                      minRows={3}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ width: "100%" }} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => setShowVerifyModal(false)} sx={{ fontWeight: 900, borderRadius: 2 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleVerify}
                  disabled={verifying}
                  sx={{ fontWeight: 1000, borderRadius: 2 }}
                >
                  {verifying ? "Verifying..." : "Submit Verification"}
                </Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default TaskManagement;
